import { Info } from "lucide-react";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { isUrl } from "@/components/helpers/url-utils";
import { isAttendees } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { MeetingAttendees } from "@/components/pieces/coaching/meetings/MeetingAttendees/MeetingAttendees";
import { MeetingGuests } from "@/components/pieces/coaching/meetings/MeetingGuests";
import { MeetingLocationMenu } from "@/components/pieces/coaching/meetings/MeetingLocationMenu";
import { getUser } from "@/components/session/JwtTokenManager";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { FullMeetingQuery } from "@/graphql";
import { FullMeeting, UpcomingMeeting } from "@/types/meetings";
import { formatDate } from "@/utils/datetime";

type MeetingUser = NonNullable<FullMeetingQuery["currentUser"]>;

type Meeting = (UpcomingMeeting | FullMeeting) & {
  formFieldAnswers?: FullMeeting["formFieldAnswers"];
};

interface MeetingSideBarProps {
  meeting: Meeting;
  currentUser?: MeetingUser | null;
}

export function MeetingSidebar({ meeting, currentUser }: MeetingSideBarProps) {
  const user = getUser();

  const isCoach = user?.id === meeting.coach?.id;
  const primaryEmail = currentUser?.primaryCalendarAccount?.accountEmail;
  const location = meeting.customLocation || meeting.location;
  const isGoogleMeetLocation = meeting.location && !meeting.customLocation;
  const isFreeCancel = meeting.refundableUntil
    ? Date.now() < new Date(meeting.refundableUntil).getTime()
    : true;

  return (
    <Box
      width={"100%"}
      backgroundColor="#FFF"
      className={CARD_CLASSES}
      p={[3, 3, 24]}
      mb={[24, 24, 24, 0]}
    >
      {meeting.cancelReason && (
        <Box mb={3}>
          <Box fontWeight={500}>Cancellation Reason</Box>
          {meeting.cancelReason}
        </Box>
      )}
      {location ? (
        <Box mb={3}>
          <Box fontWeight={500}>Session Location</Box>
          {meeting.canceledAt ? (
            <Box color={SUBTEXT_COLOR} css={{ textDecoration: "line-through" }}>
              {location}
            </Box>
          ) : (
            <Flex alignItems="center" justifyContent="space-between">
              {isUrl(location) ? (
                <a href={location} target="_blank">
                  {location}
                </a>
              ) : (
                <Box>{location}</Box>
              )}

              {isCoach && !meeting.canceledAt && currentUser && (
                <MeetingLocationMenu meeting={meeting} user={currentUser} />
              )}
            </Flex>
          )}
          {isCoach && isGoogleMeetLocation ? (
            <Box
              p={2}
              mt={2}
              width="100%"
              backgroundColor="#DBEBF5"
              color="#3F96C3"
              className="round-corners"
            >
              You must be logged in to your <b>{primaryEmail}</b> Google account
              to join the meeting
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box mb={3}>
        <Box fontWeight={500}>Session Length</Box>
        {meeting.duration} minutes
      </Box>
      {meeting?.allowInvitation ? <MeetingAttendees meeting={meeting} /> : null}

      {!isAttendees(meeting) && (
        <Box pt={24} className="bts">
          <Box fontWeight={500} mb={2}>
            Cancellation and Reschedule Policy
          </Box>
          <Tooltip
            content={`Until ${formatDate(meeting.refundableUntil, "MMM do h:mma z")}`}
          >
            <Flex alignItems="center">
              Free cancellation and reschedules until{" "}
              {meeting.cancellationWindowHours} hours before session.
              <Info className="ml-1" size={16} color={SUBTEXT_COLOR} />
            </Flex>
          </Tooltip>
          {!isFreeCancel && (
            <Box color={SUBTEXT_COLOR}>
              The free cancellation and reschedule window is now closed.
            </Box>
          )}
        </Box>
      )}
      {meeting.product?.enableGuests && <MeetingGuests meeting={meeting} />}
    </Box>
  );
}
